import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/umami"
import {
  DISMISS_KEY,
  readStorage,
  removeStorage,
  writeStorage,
  eligiblePlatform,
  dismissedRecently
} from "../lib/android_app_banner"

let shownTracked = false

// Révèle le bandeau d'installation de l'app Android, servi masqué (le HTML doit
// rester identique pour tous : voir Marketing::AndroidAppBannerComponent).
//
// Le bandeau n'écrit rien tant que l'utilisateur ne l'a pas fermé, et ne
// sonde pas le terminal (pas de getInstalledRelatedApps) : finalité
// promotionnelle, donc hors exemption (art. 82 loi 78-17). Il lit seulement de
// quoi adapter son affichage : user agent, mode d'affichage et clé de refus,
// posée par l'utilisateur lui-même (#2878).
export default class extends Controller {
  connect() {
    // Turbo remet en cache le DOM tel qu'il est au moment de quitter la page.
    // Sans ce re-masquage, un bandeau révélé — ou fermé — réapparaît en flash au
    // retour arrière avant que connect() n'ait tranché à nouveau.
    document.addEventListener("turbo:before-cache", this.#rehide)
    // ... mais une visite qui échoue (hors ligne, fetch en erreur) laisse
    // l'utilisateur sur la page courante avec le bandeau masqué et sans nouveau
    // connect() pour le rétablir. On réévalue donc sur l'échec.
    document.addEventListener("turbo:fetch-request-error", this.#reevaluate)
    this.#evaluate()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.#rehide)
    document.removeEventListener("turbo:fetch-request-error", this.#reevaluate)
  }

  dismiss() {
    // Masquer d'abord, persister ensuite : si l'écriture échoue (stockage
    // bloqué), le refus n'est pas mémorisé mais la croix ferme quand même.
    this.#rehide()
    writeStorage("localStorage", DISMISS_KEY, String(Date.now()))
    track("androidAppBanner_dismissed")
  }

  #evaluate() {
    if (!this.#eligible()) return

    this.#reveal()
    this.#trackShown()
  }

  #eligible() {
    const dismissedAt = readStorage("localStorage", DISMISS_KEY)
    // Refus expiré : la clé n'a plus d'effet, on l'efface plutôt que de la garder
    // indéfiniment sur le terminal (la politique annonce 30 jours).
    if (dismissedAt !== null && !dismissedRecently(dismissedAt)) removeStorage("localStorage", DISMISS_KEY)

    return eligiblePlatform({
      userAgent: navigator.userAgent,
      standalone: window.matchMedia("(display-mode: standalone)").matches,
      dismissedAt
    })
  }

  // Une impression par chargement complet, pas une par navigation Turbo :
  // `connect()` rejoue à chaque page et à chaque restauration de cache, ce qui
  // ferait de `shown` un compteur de pages vues et rendrait les ratios
  // dismissed/shown et clicked/shown ininterprétables. Drapeau en mémoire et non
  // en sessionStorage : le bandeau n'écrit rien tant que l'utilisateur ne l'a pas
  // fermé.
  #trackShown() {
    if (shownTracked) return

    shownTracked = true
    track("androidAppBanner_shown")
  }

  #reveal() {
    this.element.classList.remove("hidden")
  }

  #rehide = () => {
    this.element.classList.add("hidden")
  }

  #reevaluate = () => {
    this.#evaluate()
  }
}
